import endpoints from '@app/src/api/endpoints'
import { FetchKey } from '@app/src/api/fetchHooks'
import { useCreateResource } from '@app/src/api/updateHooks'
import type { DrawerViewProps } from '@app/src/components/Drawer/DrawerView'
import DrawerView from '@app/src/components/Drawer/DrawerView'
import { useAmplitude } from '@app/src/context/AmplitudeContext'
import { useSnackbar } from '@app/src/context/SnackbarContext'
import useErrorNotification from '@app/src/hooks/errorNotification'
import { useWriteAccess } from '@app/src/hooks/useWriteAccess'
import FinalRiskRatingView from '@app/src/pages/ResourceCollection/Collections/Provider/FinalRiskRatingView'
import type { FinalRiskRating } from '@app/src/types/categories'
import type { UpdateProviderFinalRiskRating } from '@app/src/types/organizations'
import { SolutionType } from '@app/src/types/resourceExplorer'
import { AmplitudeTrackingEvents, NotificationSeverity } from '@app/src/wf-constants'
import { Box } from '@mui/material'
import { useQueryClient } from '@tanstack/react-query'
import React from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useIntl } from 'react-intl'
import { useDrawer } from '../DrawerContext'

type DrawerViewRiskAssessmentProps = {
  providerIds: number[]
  finalRiskRating?: FinalRiskRating
  pageOpenedFromName?: string
} & Omit<DrawerViewProps, 'title' | 'subtitle'>

const DrawerViewRiskAssessment: React.FC<DrawerViewRiskAssessmentProps> = ({
  providerIds,
  finalRiskRating,
  pageOpenedFromName,
  ...props
}) => {
  const { formatMessage } = useIntl()
  const { mutate: saveResource, isPending: isUpdating } = useCreateResource<UpdateProviderFinalRiskRating>()
  const { trackEvent } = useAmplitude()
  const { showSnackbar } = useSnackbar()
  const { showErrorNotification } = useErrorNotification()
  const queryClient = useQueryClient()
  const { closeDrawer } = useDrawer()
  const hasWriteAccess = useWriteAccess()

  const formMethods = useForm<Omit<UpdateProviderFinalRiskRating, 'providerIds'>>({
    mode: 'onChange',
    defaultValues: {
      newFinalRiskRating: finalRiskRating,
      comment: '',
    },
  })

  const handleSaveFinalRiskRating = async (values: Omit<UpdateProviderFinalRiskRating, 'providerIds'>) => {
    saveResource(
      { url: endpoints.providerRiskRating, body: { ...values, providerIds } },
      {
        onError: requestError => {
          showErrorNotification({ requestError })
        },
        onSuccess: () => {
          showSnackbar({
            message: formatMessage({ id: 'notifications.successfulFinalRiskAssessment' }),
            severity: NotificationSeverity.success,
            disableAutoClose: true,
          })

          queryClient.invalidateQueries({ queryKey: [FetchKey.ProviderCollection] })
          queryClient.invalidateQueries({ queryKey: [FetchKey.Provider] })
          queryClient.invalidateQueries({ queryKey: [FetchKey.ProviderRisk] })
          queryClient.invalidateQueries({ queryKey: [FetchKey.ProviderActivity] })

          closeDrawer()
        },
        onSettled: () => {
          trackEvent({
            name: AmplitudeTrackingEvents.Analyze.RiskAssessment.Saved,
          })

          if (pageOpenedFromName) {
            trackEvent({
              name: AmplitudeTrackingEvents.Analyze.Assessment.RiskAssessmentFromAssessmentPages,
              eventProps: {
                page: pageOpenedFromName,
                provider_count: providerIds.length,
                solution_type: SolutionType.Sourcing,
              },
            })
          }
        },
      },
    )
  }

  return (
    <DrawerView
      title={formatMessage({ id: 'schemas.providerRisk.assessment' })}
      buttons={
        hasWriteAccess
          ? [
              {
                loading: isUpdating,
                label: formatMessage({ id: 'general.update' }),
                variant: 'contained',
                color: 'primary',
                type: 'submit',
              },
            ]
          : undefined
      }
      onFormSubmit={formMethods.handleSubmit(handleSaveFinalRiskRating)}
      {...props}
    >
      <FormProvider {...formMethods}>
        <Box px={2}>
          <FinalRiskRatingView providerIds={providerIds} />
        </Box>
      </FormProvider>
    </DrawerView>
  )
}

export default DrawerViewRiskAssessment
